import { UseToggleStateReturn } from "hooks/useToggleState";
import omit from "lodash.omit";
import { autorun, toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useState } from "react";
import { Modal } from "shared-widgets";
import { useStore } from "store";
import { ICartItem } from "types/cart";
import { IMenuItem, IMenuItemOption, IMenuItemOptionChoice } from "types/menu";

export const isOptionAddOn = (option: IMenuItemOption) => option.name.toLowerCase().includes("add");

const MenuOrderModal: FC<{ item: IMenuItem; toggle: UseToggleStateReturn; editMode?: boolean; defaultCartItem?: ICartItem }> = observer(
	({ item, toggle, editMode = false, defaultCartItem }) => {
		const { cart } = useStore();
		const [cartItem, setCartItem] = useState<ICartItem>({
			menuItemId: item?.id,
			menuItem: toJS(item),
			quantity: 1,
			price: item.price,
			options: {},
		});

		useEffect(() => {
			if (editMode && defaultCartItem) {
				setCartItem(defaultCartItem);
			}
		}, [defaultCartItem, editMode]);

		const save = () => {
			if (editMode) cart.update(cartItem);
			else cart.add(cartItem);
			toggle.off();
		};

		const handleOptionChange = useCallback(
			(option: IMenuItemOption, choice: IMenuItemOptionChoice) =>
				setCartItem((prev) => {
					const isAddOn = isOptionAddOn(option);
					return {
						...prev,
						options:
							cartItem.options[option.name + (isAddOn ? ":" + choice.name : "")]?.name === choice.name
								? omit(prev.options, option.name + (isAddOn ? ":" + choice.name : ""))
								: {
										...prev.options,
										[option.name + (isAddOn ? ":" + choice.name : "")]: choice,
								  },
					};
				}),
			[cartItem.options]
		);

		return (
			<Modal toggle={toggle} title={<span className="text-secondary">Order: [{item.titleEn}]</span>}>
				<div className="flex gap-x-4">
					<div className="flex flex-col gap-y-2">
						<img src={item.imgPath} className="w-48 h-36 object-cover rounded mt-5" alt="" />
						<div className="rounded grid grid-cols-3">
							<button
								className="bg-gray-700 bg-opacity-80 py-1 text-center rounded-l active:bg-opacity-80"
								onClick={() => setCartItem((prev) => ({ ...prev, quantity: Math.max(prev.quantity - 1, 1) }))}
							>
								-
							</button>
							<span className="border-t border-b border-gray-600 py-1 text-center">{cartItem.quantity}</span>
							<button
								className="bg-secondary py-1 text-center rounded-r active:bg-opacity-80"
								onClick={() => setCartItem((prev) => ({ ...prev, quantity: prev.quantity + 1 }))}
							>
								+
							</button>
						</div>
						<button className="bg-primary py-1.5 rounded" onClick={save}>
							{editMode ? "Save" : "Add to cart"}
						</button>
					</div>
					<div className="flex-1">
						<div className="text-sm font-medium text-secondary">Options:</div>
						<div className="mb-4 border-2 rounded border-secondary border-opacity-50 divide-opacity-50">
							{item.options.length ? (
								item.options.map((option) => (
									<div className="flex space-x-4 items-start p-2 rounded" key={option.id}>
										<span className="font-medium text-sm text-secondary">{option.name}:</span>
										<div className="grid grid-cols-2 gap-2">
											{option.choices.map((choice) => (
												<div
													className={`py-0.5 rounded px-1 border border-opacity-30 ${
														cartItem.options[isOptionAddOn(option) ? option.name + ":" + choice.name : option.name]
															?.name === choice.name
															? "bg-primary border-primary"
															: "border-secondary"
													}`}
													key={choice.id}
													onClick={() => handleOptionChange(option, choice)}
												>
													<div className="cursor-pointer text-xs">
														{choice.name} <span className="text-secondary">[+{choice.price}pts]</span>
													</div>
												</div>
											))}
										</div>
									</div>
								))
							) : (
								<div className="text-secondary flex items-center px-2 py-2 text-xs">No Options</div>
							)}
						</div>
					</div>
				</div>
			</Modal>
		);
	}
);

export default MenuOrderModal;
